import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch, validatePhoneNumber, showAlert } from '../utils/utils';

import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import AlertModal from './AlertModal';

const smsSettingsStyle = (theme) => ({
  root: {
    margin: '20px 0 0 20px',
    color: '#999',
  },
  twilioSettingsTitle: {
    fontSize: '160%',
    fontWeight: 600,
  },
  inputsWrapper: {
    marginLeft: '16px',
  },
  submitButtonDiv: {
    float: 'right',
    paddingBottom: '10px',
  },
  textFieldHolder: {
    marginTop: '16px',
  },
});

/* Call with something like:
 * <SmsSettings firmId={DA.getIntendedAccountId()} />
 */

class SmsSettings extends Component {
  constructor(props) {
    super(props);

    this.state = {
      redirect: undefined,
      firmTwilioInfoId: 0,
      accountNumber: '',
      authToken: '',
      phoneNumber: '',
    };
  }

  componentDidMount() {
    const firmId = this.props.firmId;
    // Note: this API call is safe because it requires SuperAdmin authorization
    const uri = `/api/firm/${firmId}/twilioInfo`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      this.setState({
        firmTwilioInfoId: json.firmTwilioInfoId,
        accountNumber: json.accountNumber,
        authToken: json.authToken,
        phoneNumber: json.phoneNumber,
      });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  validateAccountNumber() {
    const acctNo = this.state.accountNumber;
    if (acctNo.length === 0) {
      return { valid: false, errorMessage: 'You need to supply an Account Number' };
    }
    if (!acctNo.startsWith('AC')) {
      return { valid: false, errorMessage: "The Account Number must start with 'AC'" };
    }
    if (acctNo.length < 34) {
      return { valid: false, errorMessage: 'The supplied Account Number is too short' };
    }
    if (acctNo.length > 34) {
      return { valid: false, errorMessage: 'The supplied Account Number is too long' };
    }
    return { valid: true };
  }

  validateAuthToken() {
    const token = this.state.authToken;
    if (token.length === 0) {
      return { valid: false, errorMessage: 'You need to supply an Auth Token' };
    }
    if (token.length < 32) {
      return { valid: false, errorMessage: 'The supplied Auth Token is too short' };
    }
    if (token.length > 32) {
      return { valid: false, errorMessage: 'The supplied Auth Token is too long' };
    }
    return { valid: true };
  }

  validatePhoneNumber() {
    const phoneNumber = this.state.phoneNumber;
    if (phoneNumber.length === 0) {
      return { valid: false, errorMessage: 'You need to supply an Outgoing Phone Number' };
    } else if (!phoneNumber.startsWith('+1')) {
      return { valid: false, errorMessage: "The Outgoing Phone Number must be of the form '+1nnnnnnnnnn'" };
    } else {
      return validatePhoneNumber(phoneNumber);
    }
  }

  validateForm() {
    const accountNumberValidation = this.validateAccountNumber();
    if (!accountNumberValidation.valid) {
      return accountNumberValidation;
    }
    const authTokenValidation = this.validateAuthToken();
    if (!authTokenValidation.valid) {
      return authTokenValidation;
    }
    return this.validatePhoneNumber();
  }

  buildApiStruct() {
    return {
      id: this.state.firmTwilioInfoId,
      accountNumber: this.state.accountNumber,
      authToken: this.state.authToken,
      phoneNumber: this.state.phoneNumber,
    };
  }

  submit() {
    const validation = this.validateForm();
    if (validation.valid) {
      const firmId = this.props.firmId;
      // Note: this API call is safe because it requires SuperAdmin authorization
      const uri = `/api/firm/${firmId}/twilioInfo`;
      const apiStruct = this.buildApiStruct();
      const method = apiStruct.id > 0 ? 'put' : 'post';
      const request = createStdRequest(uri, method, apiStruct);
      const actionOnGoodResponse = (json) => {
        showAlert(this, 'Submission succeeded');
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes } = this.props;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <div className={classes.root}>
          <Grid container>
            <Grid item xs={12} md={4}>
              <div className={classes.twilioSettingsTitle}>Twilio (SMS) Settings</div>
              <div className={classes.inputsWrapper}>
                <div className={classes.textFieldHolder}>
                  <CrmTextField
                    label="Account Number"
                    fullWidth
                    value={this.state.accountNumber}
                    onChange={(event) => this.setState({ accountNumber: event.target.value.trim() })}
                  />
                </div>
                <div className={classes.textFieldHolder}>
                  <CrmTextField
                    label="Authorization Key"
                    fullWidth
                    value={this.state.authToken}
                    onChange={(event) => this.setState({ authToken: event.target.value.trim() })}
                  />
                </div>
                <div className={classes.textFieldHolder}>
                  <CrmTextField
                    label="Outgoing Phone Number"
                    fullWidth
                    value={this.state.phoneNumber}
                    onChange={(event) => this.setState({ phoneNumber: event.target.value.trim() })}
                  />
                </div>
              </div>
              <div className={classes.submitButtonDiv}>
                <CrmButton filled onClick={() => this.submit()}>
                  Submit
                </CrmButton>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }
}

const actionProducers = { setApiResponseInfo };
export default connect(null, actionProducers)(withStyles(smsSettingsStyle)(SmsSettings));
